// Gifts Profile's own privacy notices (Edward, Oct 26), adding to the Novos Privacy Policy:
//
// - /privacy: the Website Privacy Notice, for anyone reading giftsprofile.com (cookies, ad
//   tracking by region, Your privacy choices). Linked from the site's footers and cookie panels.
// - /privacy/profile: the Gifts Profile Privacy Notice, agreed on the start form (POLICY_VERSION in
//   lib/copy.ts): what the test collects, who can see results, AI, the companies used, messages,
//   deletion and rights.
//
// They describe only what the app does today. To be reviewed by a lawyer; a change in wording is a
// new date here and a new POLICY_VERSION.

import type { ReactNode } from "react";
import { Link } from "react-router";
import { Page } from "../components/Page";
import { NOVOS_PRIVACY_URL } from "../lib/copy";

const UPDATED = "October 26, 2026";

function Notice({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Page width="wide">
      <article className="mt-8 max-w-[68ch] text-[16px] leading-relaxed [&_a]:text-accent-text [&_h2]:mt-10 [&_h2]:font-[family-name:var(--font-serif)] [&_h2]:text-[22px] [&_h2]:font-extrabold [&_h2]:leading-tight [&_h2]:tracking-[-0.02em] [&_h3]:mt-6 [&_h3]:font-bold [&_li]:mt-1.5 [&_p]:mt-3 [&_ul]:mt-3 [&_ul]:list-disc [&_ul]:pl-6">
        <p className="eyebrow text-accent-text">Gifts Profile, from Novos</p>
        <h1 className="heading mt-2 text-balance text-[32px] leading-[1.1]">{title}</h1>
        <p className="!mt-2 text-[15px] text-muted">Last updated {UPDATED}</p>
        {children}
        <h2>Contact us</h2>
        <div className="mt-3 rounded-[14px] bg-soft p-4">
          <p className="!mt-0 font-bold">Novos Network</p>
          <p className="!mt-0">P.O. Box 680458, Franklin, TN 37068</p>
          <p className="!mt-0">support@novosnetwork.com</p>
        </div>
      </article>
    </Page>
  );
}

function Table({ head, rows }: { head: string[]; rows: ReactNode[][] }) {
  return (
    <div className="mt-3 overflow-x-auto">
      <table className="w-full border-collapse text-[15px]">
        <thead>
          <tr>
            {head.map((h) => (
              <th key={h} className="border-b border-line py-2 pr-3 text-left text-[12px] font-bold uppercase tracking-[0.08em] text-muted">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              {r.map((c, j) => (
                <td key={j} className={`border-b border-line py-2.5 pr-3 align-top ${j === 0 ? "font-bold" : ""}`}>
                  {c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const Code = ({ children }: { children: ReactNode }) => <code className="rounded-[6px] bg-soft px-1.5 py-0.5 text-[14px]">{children}</code>;

function RegionRules() {
  return (
    <ul>
      <li>
        <strong>In the European Union, the wider European Economic Area, the United Kingdom or Switzerland, or if we can't tell where you are:</strong> advertising cookies and
        tracking stay off unless you choose Accept on our banner.
      </li>
      <li>
        <strong>Everywhere else, including the United States:</strong> they're on unless you opt out. Use <strong>Your privacy choices</strong> at the bottom of any page, or go to{" "}
        <Link to="/privacy-choices">giftsprofile.com/privacy-choices</Link>.
      </li>
      <li>
        <strong>Global Privacy Control:</strong> if your browser sends this signal, we treat it as an opt-out, wherever you are.
      </li>
    </ul>
  );
}

export function WebsitePrivacyPage() {
  return (
    <Notice title="Website Privacy Notice">
      <p>
        This notice covers visiting giftsprofile.com: reading our pages before you start the Gifts Profile. Gifts Profile is run by Novos Network ("Novos", "we"). This notice adds to
        the <a href={NOVOS_PRIVACY_URL}>Novos Privacy Policy</a>, which also applies.
      </p>
      <p>
        When you start the Gifts Profile, the <Link to="/privacy/profile">Gifts Profile Privacy Notice</Link> applies as well. It covers your answers, your results and who can see
        them.
      </p>

      <h2>What we collect when you visit</h2>
      <ul>
        <li>Which of our pages you visit, in what order and how far down, using our own first-party cookie.</li>
        <li>
          How you found us: the campaign tags on the link you followed (such as <Code>utm_source</Code>), an ad click ID if there was one, the page you first landed on and the site that
          sent you.
        </li>
        <li>Your device type (phone, tablet or computer) and browser.</li>
        <li>
          Your IP address, which we use to keep the site secure and to work out which country's cookie rules apply to you. When we record a cookie choice, we keep only a scrambled
          (hashed) form of it.
        </li>
      </ul>
      <p>We don't ask for your name or email to read our pages.</p>

      <h2>Cookies and ad tracking</h2>
      <Table
        head={["Kind", "What they do", "Your choice"]}
        rows={[
          ["Essential", <>Remember your cookie choice (<Code>gp_consent</Code>) and keep the site secure.</>, "Always on."],
          [
            "Our own measurement",
            <>
              Count visits, remember how you found us, and keep you on the same version of a page we're testing (<Code>gp_vid</Code>, <Code>gp_attr</Code>, <Code>gp_ab</Code>). Never
              shared with advertising partners.
            </>,
            "Set for everyone; kept within Novos.",
          ],
          [
            "Advertising",
            "Cookies and similar tools from Meta and Google (such as the Meta pixel and Google Analytics) that measure our ads and help show relevant ones. Our servers can also tell Meta and Google which of our pages you reached.",
            "Depends on where you are (below).",
          ],
        ]}
      />
      <RegionRules />
      <p>Your choice is kept in your browser and applies on giftsprofile.com and app.giftsprofile.com. If you opt out, we won't ask you to opt back in for 12 months.</p>
      <p>
        <strong>Working out where you are.</strong> Our own servers look up which country your IP address belongs to in a database we keep (
        <a href="https://db-ip.com">IP Geolocation by DB-IP</a>). Your IP address isn't sent to DB-IP or anyone else for this.
      </p>

      <h2>Who we share it with</h2>
      <ul>
        <li>
          <strong>Railway</strong> hosts the site (in the United States).
        </li>
        <li>
          <strong>Meta and Google</strong> receive advertising information under your choices above.
        </li>
      </ul>
      <p>
        We don't sell your information for money. Some US state laws, including California's, treat sending information to advertising partners for targeted ads as "selling" or
        "sharing". You can opt out at any time with <Link to="/privacy-choices">Your privacy choices</Link>.
      </p>

      <h2>Your rights</h2>
      <p>
        Depending on where you live, you can ask to see, correct or delete information we hold about you, and to opt out of targeted advertising. In the EU, EEA, UK or Switzerland you
        can also object to or restrict how we use it, withdraw consent at any time, and complain to your local data protection authority. Contact us below to ask.
      </p>

      <h2>Changes</h2>
      <p>If we change this notice, we'll update the date at the top.</p>
    </Notice>
  );
}

export function ProfilePrivacyPage() {
  return (
    <Notice title="Gifts Profile Privacy Notice">
      <p>
        This notice covers taking the Gifts Profile: starting it, your answers, your results, and describing someone else when they ask you. You agree to it when you start. It adds to
        the <a href={NOVOS_PRIVACY_URL}>Novos Privacy Policy</a> and the <Link to="/privacy">Website Privacy Notice</Link>, which also apply. Where this notice is more specific, it's
        the one to go by.
      </p>

      <h2>1. What we collect</h2>
      <h3>What you give us</h3>
      <ul>
        <li>Your first name, email address and, if you choose, your phone number.</li>
        <li>What describes you best (your occupation), and, if you choose, your age range and gender.</li>
        <li>Your answers to the questions, and the choices you make on the start page (agreeing to the Terms and Privacy Policy, and any boxes you tick for emails or texts).</li>
      </ul>
      <h3>What we create from it</h3>
      <ul>
        <li>Your results (your Freedom and Clarity, your domain, your top gifts), your personal write-up, and a PDF of your results.</li>
        <li>How long you spent on each part of the questions, which helps us improve the assessment.</li>
      </ul>
      <h3>From the people who describe you</h3>
      <p>
        If you ask people to describe you, we collect their answers about you. We don't ask them for their name or email; their link is tied to their browser with a cookie so their
        answers are saved.
      </p>
      <h3>When you describe someone else</h3>
      <p>If someone asks you to describe them, we keep your answers about them. They only ever see a combined view from everyone who answered, never your individual answers.</p>
      <h3>Also collected</h3>
      <ul>
        <li>How you found us and the event or link you came through, as described in the Website Privacy Notice.</li>
        <li>
          <strong>Payments</strong> are handled by Stripe. We don't see or store your card details. We receive whether you paid, the amount, and any code you used.
        </li>
      </ul>

      <h2>2. How we use it</h2>
      <ul>
        <li>To give you your Gifts Profile: your results, your personal write-up and your PDF, by email and on the site.</li>
        <li>To combine the views of the people who describe you, and to send you your updated results when they've answered.</li>
        <li>To run event links, personal links and team links.</li>
        <li>To take payment, and to apply event prices and codes.</li>
        <li>To keep a record of what you agreed to, and to send you emails or texts only if you agreed (section 6).</li>
        <li>To understand which campaigns and events bring people in, and to measure our advertising (section 7).</li>
        <li>To keep the service secure and working, and to improve the assessment using combined statistics.</li>
      </ul>

      <h2>3. Your personal write-up and AI</h2>
      <p>
        Your personal write-up is written by an AI model: Claude, made by Anthropic. To write it, we send Anthropic your results, a few phrases from your answers, and the combined view
        from the people who described you. We don't send your name, email address, phone number or anything else that says who you are: the write-up is written with a placeholder
        where your name goes, and we put your first name in afterwards, on our own systems. Anthropic processes this to write the text and returns it to us. How Anthropic handles it is
        governed by Anthropic's commercial terms with us. The write-up is labelled as written by AI.
      </p>
      <p>
        You can also choose to connect an AI app you use (such as Claude or ChatGPT) to your own Gifts Profile. If you do, that app can read the results you allow it to, and its
        provider handles them under its own terms. You can disconnect it at any time.
      </p>

      <h2>4. Who can see your results</h2>
      <ul>
        <li>
          <strong>You.</strong> On the site, by email, and in your PDF.
        </li>
        <li>
          <strong>A team leader, if you agree.</strong> If you take it through a team link and agree to share, that team's leader can see your results.
        </li>
        <li>
          <strong>Not the people who describe you.</strong> They don't see your results, and they don't see each other's answers.
        </li>
        <li>
          <strong>Authorised staff and contractors.</strong> Novos staff, and contractors Novos authorises to build, run, support and maintain Gifts Profile, can see results when their
          work needs it: for example to help you, run an event, or fix a problem. Their access needs a sign-in with two-factor authentication.
        </li>
        <li>
          <strong>The companies in section 5</strong>, for the jobs they do for us.
        </li>
      </ul>

      <h2>5. Who we share it with</h2>
      <Table
        head={["Company", "What for"]}
        rows={[
          ["Railway", "Hosting the app and the database (in the United States)."],
          ["Anthropic", "Writing your personal write-up (section 3)."],
          ["Postmark", "Sending emails, including your results and PDF."],
          ["Stripe", "Taking payment."],
          [
            "HubSpot",
            "Our contact records: your name, email, phone if given, how you found us, the event or link you came through, and what you agreed to (Terms, emails, texts and ad tracking).",
          ],
          ["Aloware", "Texts and calls, only if you ticked the box to agree to them."],
          [
            "Meta and Google",
            "Measuring and improving our advertising, under your ad tracking choices (section 7). When you start, check out, pay for or finish the profile, our servers can tell them, along with a scrambled (hashed) form of your email address.",
          ],
        ]}
      />
      <p>
        We don't sell your information for money. Some US state laws, including California's, treat sending information to advertising partners for targeted ads as "selling" or
        "sharing". You can opt out at any time with <Link to="/privacy-choices">Your privacy choices</Link>.
      </p>
      <p>We may also share information when the law requires it, to protect people's safety or our rights, or as part of a sale or reorganization of the business.</p>

      <h2>6. Emails and texts</h2>
      <ul>
        <li>
          <strong>Your results.</strong> We email you your results and PDF, and an update when the people who describe you have answered. These are part of the service.
        </li>
        <li>
          <strong>Marketing emails.</strong> Only if you tick "Send me occasional emails from Novos". This adds you to our "Gifts Profile" email list. Every marketing email has an
          unsubscribe link.
        </li>
        <li>
          <strong>Texts and calls.</strong> Only if you tick the box to agree, from "Gifts Profile by Novos". Reply STOP to stop at any time, or HELP for help. Agreeing isn't required
          to buy anything.
        </li>
      </ul>

      <h2>7. Cookies and ad tracking</h2>
      <p>
        The app uses the same rules as the website (see the <Link to="/privacy">Website Privacy Notice</Link>), plus essential cookies that keep you signed in, save your place in the
        questions and tie a link to describe someone to your browser (<Code>gp_take</Code>, <Code>gp_person</Code>, <Code>gp_rater</Code>, and sign-in cookies).
      </p>
      <RegionRules />
      <p>
        If we know who you are, your ad tracking choice is also recorded on your contact record, so we leave you out of advertising audiences if you opt out. Choosing No never affects
        your profile or your results.
      </p>

      <h2>8. Keeping and deleting it</h2>
      <p>
        We keep your Gifts Profile so you can come back to your results and see updates as the people who describe you answer. If you'd like us to delete your profile and your
        answers, contact us. We may keep some records after that where the law requires it, such as records of payments and of what you agreed to.
      </p>

      <h2>9. Your rights, age and security</h2>
      <ul>
        <li>
          <strong>Your rights.</strong> Depending on where you live, you can ask to see your information and get a copy, correct it, have it deleted, and opt out of targeted
          advertising. In the EU, EEA, UK or Switzerland you can also object to or restrict how we use it, move it to another service, withdraw consent at any time, and complain to your
          local data protection authority. We'll check it's you, usually by email, before we act on a request, and we won't treat you differently for using these rights.
        </li>
        <li>
          <strong>Age.</strong> Gifts Profile is for people 18 and over. Everyone confirms they're 18 or older before starting.
        </li>
        <li>
          <strong>Where it's stored.</strong> In the United States. Some of the companies in section 5 may process it in other countries under their own safeguards.
        </li>
        <li>
          <strong>Security.</strong> Connections to Gifts Profile are encrypted, and staff and contractor access to results needs a sign-in with two-factor authentication. No system is
          perfectly secure, so please contact us if you think something is wrong.
        </li>
      </ul>

      <h2>10. Changes</h2>
      <p>
        If we change this notice, we'll update the date at the top. If a change is significant, we'll make it clear on the site, and people starting a new Gifts Profile will agree to
        the new version.
      </p>
    </Notice>
  );
}
